import { useIntl } from 'react-intl';

import { PERIOD_TYPE, ActiveStayPeriod } from 'src/features/stay/types';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { Separator } from 'src/registry/ui/Separator';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import CellStayPeriodZones from 'src/features/stay/components/cell/CellStayPeriodZones';
import { zoneRuleStayTypeTranslationMap } from 'src/features/membershipType/utils/constants.ts';
import Duration from 'src/features/visitor/components/Duration';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PATH } from 'src/features/navigation/utils/constants';
import { ICON_MAP } from 'src/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';

type StayPeriodsProps = {
    stayPeriodsData?: ActiveStayPeriod[];
};

const ActiveStayPeriods = ({ stayPeriodsData }: StayPeriodsProps) => {
    const { formatMessage, locale } = useIntl();
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);
    const accessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);

    const periodCount = stayPeriodsData?.length ?? 0;

    return (
        <>
            {stayPeriodsData?.map((period, index) => {
                return (
                    <div className="mt-4 grid gap-2" key={period.id}>
                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {`${formatMessage(visitorMessages.period)} (${periodCount - index})`}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.zones} fallbackMessage="-" className="text-sm">
                                {period?.zones?.length > 0 ? (
                                    <CellStayPeriodZones zonesData={period?.zones} className="font-normal" />
                                ) : (
                                    '-'
                                )}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.start)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.start} fallbackMessage="-" className="text-sm">
                                {getFormattedDateTimeForDetailPanel(period.start)}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.end)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.end} fallbackMessage="-" className="text-sm">
                                {getFormattedDateTimeForDetailPanel(period.end ?? undefined)}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>

                        <GeneralBlockAttribute
                            label={formatMessage(visitorMessages.duration)}
                            className="flex flex-row items-center gap-1 align-baseline"
                        >
                            <Duration entryTime={period.start} exitTime={period.end ?? undefined} />
                        </GeneralBlockAttribute>

                        <SidePanelBodyAttribute className="flex flex-row items-center gap-1 align-baseline">
                            <SidePanelBodyAttributeTitle>
                                {formatMessage(visitorMessages.type)}
                            </SidePanelBodyAttributeTitle>
                            <SidePanelBodyAttributeBody control={period.type} fallbackMessage="-" className="text-sm">
                                {period.type
                                    ? formatMessage(zoneRuleStayTypeTranslationMap[period.type as PERIOD_TYPE])
                                    : '-'}
                            </SidePanelBodyAttributeBody>
                        </SidePanelBodyAttribute>
                        <GeneralBlockAttribute
                            label={formatMessage(visitorMessages.tariff)}
                            value={
                                period.pricing_plan?.translations?.find(
                                    (translation) => translation.language === locale,
                                )?.name ?? '-'
                            }
                            link={
                                accessPricingPlans && period.pricing_plan?.id
                                    ? `/${PATH.ADMINISTRATION}/pricing-plan/${period.pricing_plan?.id}`
                                    : undefined
                            }
                            icon={accessPricingPlans && period.pricing_plan?.id ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                            className="flex flex-row items-center gap-1 align-baseline"
                        />
                        <GeneralBlockAttribute
                            label={formatMessage(membershipMessages.membership)}
                            value={period.membership?.name ?? period.membership?.ref ?? '-'}
                            link={
                                accessMemberships && period.membership?.id
                                    ? `/${PATH.MEMBERSHIPS}/all/${period.membership?.id}`
                                    : undefined
                            }
                            icon={accessMemberships && period.membership?.id ? ICON_MAP.ARROW_UP_RIGHT : undefined}
                            className="flex flex-row items-center gap-1 align-baseline"
                        />

                        <Separator />
                    </div>
                );
            })}

            {stayPeriodsData?.length === 0 && <div>{formatMessage(visitorMessages.noPeriods)}</div>}
        </>
    );
};

export default ActiveStayPeriods;
